import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Globe,
  Briefcase,
  Award,
  Bitcoin,
  Cpu,
  BarChart3,
  Gamepad2,
  Loader2,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  XCircle,
  Trophy,
  Banknote,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Typography } from '../styles/primitives';
import { fetchAllPolymarketMarkets, fetchMarketPricesByIds, FormattedMarket } from '../services/polymarketApi';
import { supabase } from '../lib/supabaseClient';
import EventMarketCard from './EventMarketCard';
import EventDetailView from './EventDetailView';

interface EventBet {
  id: string;
  user_id: string;
  outcome_id: string;
  event_id: string;
  amount: number;
  shares: number;
  price_at_bet: number;
  chosen_outcome: 'yes' | 'no';
  settlement_status: 'active' | 'won' | 'lost' | 'cashed_out';
  payout: number;
  settled_at: string | null;
  created_at: string;
  event_question?: string;
  outcome_name?: string;
  current_price?: number;
}

interface PolymarketEventBettingProps {
  usdtBalance: number;
  userId: string;
  onBalanceRefresh: () => Promise<void>;
}

const PolymarketEventBetting: React.FC<PolymarketEventBettingProps> = ({
  usdtBalance,
  userId,
  onBalanceRefresh,
}) => {
  const { t } = useTranslation();
  const [markets, setMarkets] = useState<FormattedMarket[]>([]);
  const [filteredMarkets, setFilteredMarkets] = useState<FormattedMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const MARKETS_PER_PAGE = 20;
  const [selectedMarket, setSelectedMarket] = useState<FormattedMarket | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myBets, setMyBets] = useState<EventBet[]>([]);
  const [activeTab, setActiveTab] = useState<'markets' | 'mybets'>('markets');
  const [betSuccess, setBetSuccess] = useState<string | null>(null);
  const [cashingOutId, setCashingOutId] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: t('events.categories.all', 'All'), icon: Globe },
    { id: 'politics', name: t('events.categories.politics', 'Politics'), icon: Briefcase },
    { id: 'sports', name: t('events.categories.sports', 'Sports'), icon: Award },
    { id: 'crypto', name: t('events.categories.crypto', 'Crypto'), icon: Bitcoin },
    { id: 'economy', name: t('events.categories.economy', 'Economy'), icon: BarChart3 },
    { id: 'tech', name: t('events.categories.tech', 'Tech'), icon: Cpu },
    { id: 'general', name: t('events.categories.general', 'General'), icon: Gamepad2 },
  ];

  const activeBetsRef = useRef(myBets);
  activeBetsRef.current = myBets;

  const marketsRef = useRef(markets);
  marketsRef.current = markets;

  const refreshBetPrices = useCallback(async () => {
    const active = activeBetsRef.current.filter(b => b.settlement_status === 'active');
    if (active.length === 0) return;

    const outcomeIds = [...new Set(active.map(b => b.outcome_id))];
    const { data: outcomeRows } = await supabase
      .from('event_outcomes')
      .select('id, polymarket_id, current_price, event_id')
      .in('id', outcomeIds);

    if (!outcomeRows || outcomeRows.length === 0) return;

    const eventIds = [...new Set(outcomeRows.map(o => o.event_id))];
    const { data: eventRows } = await supabase
      .from('events')
      .select('id, polymarket_id')
      .in('id', eventIds);

    const eventPolyIdMap = new Map((eventRows || []).map(e => [e.id, e.polymarket_id]));
    const loadedMarketsMap = new Map(marketsRef.current.map(m => [m.id, m]));
    const polymarketIds = [...new Set(
      eventIds.map(eid => eventPolyIdMap.get(eid)).filter(Boolean) as string[]
    )];

    const livePrices = polymarketIds.length > 0
      ? await fetchMarketPricesByIds(polymarketIds)
      : new Map<string, Array<{ name: string; price: number }>>();

    const priceMap = new Map<string, number>();

    for (const oc of outcomeRows) {
      const polymarketEventId = eventPolyIdMap.get(oc.event_id);
      if (!polymarketEventId) {
        priceMap.set(oc.id, parseFloat(String(oc.current_price)));
        continue;
      }

      const outcomeName = oc.polymarket_id?.replace(`${polymarketEventId}_`, '');

      const apiOutcomes = livePrices.get(polymarketEventId);
      if (apiOutcomes) {
        const matched = apiOutcomes.find(o => o.name === outcomeName);
        if (matched && matched.price > 0) {
          priceMap.set(oc.id, matched.price);
          supabase
            .from('event_outcomes')
            .update({ current_price: matched.price })
            .eq('id', oc.id)
            .then(() => {});
          continue;
        }
      }

      const loadedMarket = loadedMarketsMap.get(polymarketEventId);
      if (loadedMarket) {
        const matched = loadedMarket.outcomes.find(o => o.name === outcomeName);
        if (matched && matched.price > 0) {
          priceMap.set(oc.id, matched.price);
          supabase
            .from('event_outcomes')
            .update({ current_price: matched.price })
            .eq('id', oc.id)
            .then(() => {});
          continue;
        }
      }

      priceMap.set(oc.id, parseFloat(String(oc.current_price)));
    }

    setMyBets(prev => prev.map(bet => {
      const newPrice = priceMap.get(bet.outcome_id);
      if (newPrice != null && bet.settlement_status === 'active') {
        return { ...bet, current_price: newPrice };
      }
      return bet;
    }));
  }, []);

  useEffect(() => {
    loadMarkets();
    loadMyBets();
  }, []);

  useEffect(() => {
    const marketInterval = setInterval(() => {
      fetchAllPolymarketMarkets({ limit: 150 }).then(data => {
        if (data.length > 0) setMarkets(data);
      }).catch(() => {});
    }, 30000);

    return () => clearInterval(marketInterval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(refreshBetPrices, 2000);
    const betPriceInterval = setInterval(refreshBetPrices, 15000);
    return () => {
      clearTimeout(timer);
      clearInterval(betPriceInterval);
    };
  }, [refreshBetPrices]);

  useEffect(() => {
    filterMarkets();
    setCurrentPage(1);
  }, [markets, selectedCategory, searchTerm]);

  useEffect(() => {
    if (betSuccess) {
      const timer = setTimeout(() => setBetSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [betSuccess]);

  const loadMarkets = async () => {
    try {
      setLoading(true);
      const data = await fetchAllPolymarketMarkets({ limit: 150 });
      setMarkets(data);
    } catch (error) {
      console.error('Error loading markets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyBets = async () => {
    try {
      const { data, error } = await supabase
        .from('event_bets')
        .select(`
          id, user_id, outcome_id, event_id, amount, shares,
          price_at_bet, chosen_outcome, settlement_status, payout,
          settled_at, created_at, side,
          events!event_bets_event_id_fkey ( question ),
          event_outcomes!event_bets_outcome_id_fkey ( outcome_name, current_price )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        const { data: fallback } = await supabase
          .from('event_bets')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (fallback) {
          setMyBets(fallback.map((b: Record<string, unknown>) => ({
            ...b,
            chosen_outcome: (b.chosen_outcome as string) || (b.side === 'buy' ? 'yes' : 'no'),
            settlement_status: (b.settlement_status as string) || 'active',
            payout: (b.payout as number) || 0,
          })) as EventBet[]);
        }
        return;
      }

      if (data) {
        setMyBets(data.map((b: Record<string, unknown>) => {
          const evt = b.events as Record<string, unknown> | null;
          const out = b.event_outcomes as Record<string, unknown> | null;
          return {
            ...b,
            event_question: evt?.question as string | undefined,
            outcome_name: out?.outcome_name as string | undefined,
            current_price: out?.current_price != null ? parseFloat(String(out.current_price)) : undefined,
            chosen_outcome: (b.chosen_outcome as string) || (b.side === 'buy' ? 'yes' : 'no'),
            settlement_status: (b.settlement_status as string) || 'active',
            payout: (b.payout as number) || 0,
          };
        }) as EventBet[]);
      }
    } catch (error) {
      console.error('Error loading bets:', error);
    }
  };

  const filterMarkets = () => {
    let filtered = markets;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((m) => m.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.question.toLowerCase().includes(term) ||
          (m.description && m.description.toLowerCase().includes(term))
      );
    }

    setFilteredMarkets(filtered);
  };

  const handleBet = async (outcomeIndex: number, amount: number, yesOrNo: 'yes' | 'no') => {
    if (isSubmitting || !selectedMarket) return;

    setIsSubmitting(true);
    try {
      const outcome = selectedMarket.outcomes[outcomeIndex];
      if (!outcome || amount > usdtBalance || amount <= 0) return;

      const price = yesOrNo === 'yes' ? outcome.price : (1 - outcome.price);
      if (price <= 0 || price >= 1) return;
      const shares = amount / price;

      const { data: existingEvent } = await supabase
        .from('events')
        .select('id, status')
        .eq('polymarket_id', selectedMarket.id)
        .maybeSingle();

      if (existingEvent?.status === 'resolved') {
        setBetSuccess('This market has already been resolved.');
        return;
      }

      let eventId: string;
      if (existingEvent) {
        eventId = existingEvent.id;
      } else {
        const { data: newEvent, error: eventError } = await supabase
          .from('events')
          .insert({
            polymarket_id: selectedMarket.id,
            question: selectedMarket.question,
            description: selectedMarket.description || null,
            category: selectedMarket.category,
            end_date: selectedMarket.endDate || null,
            status: 'open',
            volume: selectedMarket.volume || 0,
          })
          .select()
          .single();

        if (eventError) throw eventError;
        eventId = newEvent.id;
      }

      const outcomePolymarketId = `${selectedMarket.id}_${outcome.name}`;
      const { data: outcomeData, error: outcomeError } = await supabase
        .from('event_outcomes')
        .upsert({
          event_id: eventId,
          outcome_name: outcome.name,
          current_price: outcome.price,
          polymarket_id: outcomePolymarketId,
          total_volume: 0,
        }, { onConflict: 'polymarket_id' })
        .select()
        .single();

      if (outcomeError) throw outcomeError;

      const { error: betError } = await supabase.from('event_bets').insert({
        user_id: userId,
        outcome_id: outcomeData.id,
        event_id: eventId,
        amount,
        shares,
        price_at_bet: price,
        side: yesOrNo === 'yes' ? 'buy' : 'sell',
        chosen_outcome: yesOrNo,
        settlement_status: 'active',
        status: 'completed',
      });

      if (betError) throw betError;

      const { error: balanceError } = await supabase
        .from('balances')
        .update({ usdt_balance: usdtBalance - amount })
        .eq('user_id', userId);

      if (balanceError) throw balanceError;

      await supabase.from('transactions').insert({
        user_id: userId,
        type: 'trade',
        amount: -amount,
        description: `Event bet: ${yesOrNo.toUpperCase()} on "${selectedMarket.question}" at ${(price * 100).toFixed(1)}c`,
        status: 'completed',
      });

      await onBalanceRefresh();
      await loadMyBets();

      setBetSuccess(`Bet placed: $${amount.toFixed(2)} on ${yesOrNo.toUpperCase()} - ${(shares).toFixed(2)} shares at ${(price * 100).toFixed(1)}c`);
    } catch (error) {
      console.error('Error placing bet:', error);
      setBetSuccess('Failed to place bet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCashOut = async (betId: string) => {
    if (cashingOutId) return;
    setCashingOutId(betId);
    try {
      const bet = myBets.find(b => b.id === betId);
      if (!bet || bet.settlement_status !== 'active') return;

      const { data: outcomeData } = await supabase
        .from('event_outcomes')
        .select('current_price')
        .eq('id', bet.outcome_id)
        .maybeSingle();

      const currentPrice = outcomeData?.current_price != null
        ? parseFloat(String(outcomeData.current_price))
        : bet.current_price ?? bet.price_at_bet;

      const cashOutValue = bet.chosen_outcome === 'yes'
        ? bet.shares * currentPrice
        : bet.shares * (1 - currentPrice);

      const fee = cashOutValue * 0.01;
      const payout = Math.max(0, parseFloat((cashOutValue - fee).toFixed(2)));

      const { data: updatedBet, error: betUpdateError } = await supabase
        .from('event_bets')
        .update({
          settlement_status: 'cashed_out',
          payout,
          settled_at: new Date().toISOString(),
        })
        .eq('id', betId)
        .eq('user_id', userId)
        .select('id')
        .maybeSingle();

      if (betUpdateError) throw betUpdateError;
      if (!updatedBet) throw new Error('Bet update failed');

      const { data: balanceRow } = await supabase
        .from('balances')
        .select('usdt_balance')
        .eq('user_id', userId)
        .maybeSingle();

      const currentBalance = balanceRow?.usdt_balance ?? usdtBalance;
      const { error: balanceError } = await supabase
        .from('balances')
        .update({ usdt_balance: parseFloat(currentBalance) + payout })
        .eq('user_id', userId);

      if (balanceError) throw balanceError;

      await supabase.from('transactions').insert({
        user_id: userId,
        type: 'trade',
        amount: payout,
        description: `Cashed out bet on "${bet.event_question || 'Event'}" - ${bet.chosen_outcome?.toUpperCase()} at ${(currentPrice * 100).toFixed(1)}c`,
        status: 'completed',
      });

      await onBalanceRefresh();
      await loadMyBets();

      const netPnl = payout - bet.amount;
      setBetSuccess(`Cashed out $${payout.toFixed(2)} (${netPnl >= 0 ? '+' : ''}${netPnl.toFixed(2)} P&L)`);
    } catch (error) {
      console.error('Error cashing out:', error);
      setBetSuccess('Failed to cash out. Please try again.');
    } finally {
      setCashingOutId(null);
    }
  };

  const getRelatedMarkets = (market: FormattedMarket): FormattedMarket[] => {
    return markets
      .filter((m) => m.id !== market.id && m.category === market.category)
      .slice(0, 4);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/95 flex items-center justify-center">
        <div className="text-center">
          <Loader2
            size={40}
            className="animate-spin mx-auto mb-4"
            style={{ color: '#d91222' }}
          />
          <p style={{ fontSize: Typography.BodyMedium, color: '#64748b' }}>
            Loading markets...
          </p>
        </div>
      </div>
    );
  }

  if (selectedMarket) {
    return (
      <div className="min-h-screen bg-slate-50/95">
        {betSuccess && (
          <div
            className="fixed top-4 right-4 z-50 px-4 py-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2"
            style={{
              background: betSuccess.startsWith('Failed') ? '#fef2f2' : '#f0fdf4',
              border: betSuccess.startsWith('Failed') ? '1px solid #fecaca' : '1px solid #bbf7d0',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              maxWidth: '400px',
            }}
          >
            {betSuccess.startsWith('Failed') ? (
              <XCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
            ) : (
              <CheckCircle2 size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
            )}
            <span style={{
              fontSize: Typography.Caption,
              color: betSuccess.startsWith('Failed') ? '#991b1b' : '#166534',
              fontWeight: Typography.WeightSemibold,
            }}>
              {betSuccess}
            </span>
          </div>
        )}
        <div className="p-4 md:p-8 max-w-6xl mx-auto">
          <EventDetailView
            market={selectedMarket}
            relatedMarkets={getRelatedMarkets(selectedMarket)}
            onBack={() => setSelectedMarket(null)}
            onRelatedClick={(m) => {
              setSelectedMarket(m);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            usdtBalance={usdtBalance}
            onBet={handleBet}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    );
  }

  const activeBets = myBets.filter(b => b.settlement_status === 'active');
  const settledBets = myBets.filter(b => b.settlement_status === 'won' || b.settlement_status === 'lost' || b.settlement_status === 'cashed_out');

  return (
    <div className="min-h-screen bg-slate-50/95">
      {betSuccess && (
        <div
          className="fixed top-4 right-4 z-50 px-4 py-3 flex items-center gap-2"
          style={{
            background: betSuccess.startsWith('Failed') ? '#fef2f2' : '#f0fdf4',
            border: betSuccess.startsWith('Failed') ? '1px solid #fecaca' : '1px solid #bbf7d0',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxWidth: '400px',
          }}
        >
          {betSuccess.startsWith('Failed') ? (
            <XCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
          ) : (
            <CheckCircle2 size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
          )}
          <span style={{
            fontSize: Typography.Caption,
            color: betSuccess.startsWith('Failed') ? '#991b1b' : '#166534',
            fontWeight: Typography.WeightSemibold,
          }}>
            {betSuccess}
          </span>
        </div>
      )}
      <div className="p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1
                style={{
                  fontSize: Typography.DisplayMedium,
                  fontWeight: Typography.WeightBold,
                  color: '#0f172a',
                }}
              >
                Prediction Markets
              </h1>
            </div>
            <p style={{ fontSize: Typography.BodySmall, color: '#64748b' }}>
              Bet on real-world events with live market data
            </p>
          </div>

          <div
            className="px-5 py-3"
            style={{
              background: '#ffffff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                fontSize: Typography.Fine,
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: Typography.WeightSemibold,
                marginBottom: '2px',
              }}
            >
              {t('wallet.availableBalance', 'Available Balance')}
            </div>
            <div
              className="font-mono"
              style={{
                fontSize: Typography.HeadlineMedium,
                fontWeight: Typography.WeightBold,
                color: '#0f172a',
              }}
            >
              ${usdtBalance.toFixed(2)}
            </div>
          </div>
        </div>

        <div className="flex gap-1 mb-6 p-1" style={{
          background: 'rgba(0,0,0,0.04)',
          borderRadius: '10px',
          width: 'fit-content',
        }}>
          <button
            onClick={() => setActiveTab('markets')}
            className="px-4 py-2 transition-all duration-150"
            style={{
              background: activeTab === 'markets' ? '#ffffff' : 'transparent',
              color: activeTab === 'markets' ? '#0f172a' : '#64748b',
              fontSize: Typography.Caption,
              fontWeight: Typography.WeightSemibold,
              borderRadius: '8px',
              boxShadow: activeTab === 'markets' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            Markets
          </button>
          <button
            onClick={() => setActiveTab('mybets')}
            className="px-4 py-2 transition-all duration-150 flex items-center gap-2"
            style={{
              background: activeTab === 'mybets' ? '#ffffff' : 'transparent',
              color: activeTab === 'mybets' ? '#0f172a' : '#64748b',
              fontSize: Typography.Caption,
              fontWeight: Typography.WeightSemibold,
              borderRadius: '8px',
              boxShadow: activeTab === 'mybets' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            My Bets
            {activeBets.length > 0 && (
              <span className="px-1.5 py-0.5" style={{
                background: '#d91222',
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: Typography.WeightBold,
                borderRadius: '6px',
                minWidth: '18px',
                textAlign: 'center',
              }}>
                {activeBets.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'markets' && (
          <>
            <div className="mb-6 space-y-3">
              <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className="flex items-center gap-1.5 px-3.5 py-2 transition-all duration-150 whitespace-nowrap flex-shrink-0"
                      style={{
                        background: isActive ? '#d91222' : '#ffffff',
                        color: isActive ? '#ffffff' : '#475569',
                        fontSize: Typography.Caption,
                        fontWeight: Typography.WeightSemibold,
                        borderRadius: '8px',
                        border: isActive ? '1px solid #d91222' : '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      <Icon size={14} />
                      {cat.name}
                    </button>
                  );
                })}
              </div>

              <div className="relative">
                <Search
                  className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  size={16}
                  style={{ color: '#94a3b8' }}
                />
                <input
                  type="text"
                  placeholder="Search markets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 focus:outline-none"
                  style={{
                    background: '#ffffff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '10px',
                    fontSize: Typography.BodySmall,
                    color: '#0f172a',
                  }}
                />
              </div>
            </div>

            {(() => {
              const totalPages = Math.ceil(filteredMarkets.length / MARKETS_PER_PAGE);
              const startIdx = (currentPage - 1) * MARKETS_PER_PAGE;
              const pageMarkets = filteredMarkets.slice(startIdx, startIdx + MARKETS_PER_PAGE);

              return filteredMarkets.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span style={{ fontSize: Typography.Caption, color: '#94a3b8' }}>
                      Showing {startIdx + 1}-{Math.min(startIdx + MARKETS_PER_PAGE, filteredMarkets.length)} of {filteredMarkets.length} markets
                    </span>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{
                            background: currentPage === 1 ? 'transparent' : 'rgba(0,0,0,0.04)',
                            color: currentPage === 1 ? '#cbd5e1' : '#475569',
                            cursor: currentPage === 1 ? 'default' : 'pointer',
                          }}
                        >
                          <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                          .reduce<(number | 'gap')[]>((acc, p, i, arr) => {
                            if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('gap');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((item, i) =>
                            item === 'gap' ? (
                              <span key={`gap-${i}`} style={{ color: '#94a3b8', fontSize: '12px', padding: '0 2px' }}>...</span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => setCurrentPage(item as number)}
                                className="min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors"
                                style={{
                                  background: currentPage === item ? '#0f172a' : 'transparent',
                                  color: currentPage === item ? '#ffffff' : '#475569',
                                }}
                              >
                                {item}
                              </button>
                            )
                          )}
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{
                            background: currentPage === totalPages ? 'transparent' : 'rgba(0,0,0,0.04)',
                            color: currentPage === totalPages ? '#cbd5e1' : '#475569',
                            cursor: currentPage === totalPages ? 'default' : 'pointer',
                          }}
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {pageMarkets.map((market) => (
                      <EventMarketCard
                        key={market.id}
                        market={market}
                        onClick={() => setSelectedMarket(market)}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 mt-6">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          background: currentPage === 1 ? 'transparent' : 'rgba(0,0,0,0.04)',
                          color: currentPage === 1 ? '#cbd5e1' : '#475569',
                          cursor: currentPage === 1 ? 'default' : 'pointer',
                        }}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-xs" style={{ color: '#94a3b8' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          background: currentPage === totalPages ? 'transparent' : 'rgba(0,0,0,0.04)',
                          color: currentPage === totalPages ? '#cbd5e1' : '#475569',
                          cursor: currentPage === totalPages ? 'default' : 'pointer',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Activity
                    size={40}
                    style={{ color: '#cbd5e1', margin: '0 auto 1rem' }}
                  />
                  <h3
                    className="mb-2"
                    style={{
                      fontSize: Typography.HeadlineSmall,
                      fontWeight: Typography.WeightBold,
                      color: '#475569',
                    }}
                  >
                    No markets found
                  </h3>
                  <p style={{ fontSize: Typography.BodySmall, color: '#94a3b8' }}>
                    Try adjusting your search or category filter
                  </p>
                </div>
              );
            })()}
          </>
        )}

        {activeTab === 'mybets' && (
          <div className="space-y-6">
            {activeBets.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 mb-3" style={{
                  fontSize: Typography.BodyMedium,
                  fontWeight: Typography.WeightBold,
                  color: '#0f172a',
                }}>
                  <Clock size={16} style={{ color: '#f59e0b' }} />
                  Active Bets ({activeBets.length})
                </h3>
                <div className="space-y-3">
                  {activeBets.map((bet) => {
                    const currentVal = bet.current_price != null
                      ? (bet.chosen_outcome === 'yes'
                          ? bet.shares * bet.current_price
                          : bet.shares * (1 - bet.current_price))
                      : bet.amount;
                    const unrealizedPnl = currentVal - bet.amount;
                    const pnlPercent = bet.amount > 0 ? (unrealizedPnl / bet.amount) * 100 : 0;

                    return (
                      <div
                        key={bet.id}
                        className="p-4"
                        style={{
                          background: '#ffffff',
                          border: '1px solid rgba(0,0,0,0.08)',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        }}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <p style={{
                              fontSize: Typography.BodySmall,
                              fontWeight: Typography.WeightSemibold,
                              color: '#0f172a',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {bet.event_question || 'Event'}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5" style={{
                                background: bet.chosen_outcome === 'yes'
                                  ? 'rgba(16, 185, 129, 0.1)'
                                  : 'rgba(239, 68, 68, 0.08)',
                                color: bet.chosen_outcome === 'yes' ? '#059669' : '#dc2626',
                                fontSize: Typography.Fine,
                                fontWeight: Typography.WeightBold,
                                borderRadius: '4px',
                              }}>
                                {bet.chosen_outcome?.toUpperCase()}
                              </span>
                              {bet.outcome_name && (
                                <span style={{
                                  fontSize: Typography.Fine,
                                  color: '#94a3b8',
                                }}>
                                  {bet.outcome_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono" style={{
                              fontSize: Typography.BodySmall,
                              fontWeight: Typography.WeightBold,
                              color: unrealizedPnl >= 0 ? '#059669' : '#dc2626',
                            }}>
                              {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)}
                            </div>
                            <div className="font-mono" style={{
                              fontSize: Typography.Fine,
                              color: unrealizedPnl >= 0 ? '#10b981' : '#ef4444',
                            }}>
                              {pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between" style={{
                          paddingTop: '8px',
                          borderTop: '1px solid rgba(0,0,0,0.05)',
                        }}>
                          <div className="flex gap-4">
                            <div>
                              <div style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>Wagered</div>
                              <div className="font-mono" style={{
                                fontSize: Typography.Caption,
                                fontWeight: Typography.WeightSemibold,
                                color: '#475569',
                              }}>
                                ${bet.amount.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>Shares</div>
                              <div className="font-mono" style={{
                                fontSize: Typography.Caption,
                                fontWeight: Typography.WeightSemibold,
                                color: '#475569',
                              }}>
                                {bet.shares.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>Entry</div>
                              <div className="font-mono" style={{
                                fontSize: Typography.Caption,
                                fontWeight: Typography.WeightSemibold,
                                color: '#475569',
                              }}>
                                {(bet.price_at_bet * 100).toFixed(1)}c
                              </div>
                            </div>
                          </div>
                          <div className="font-mono" style={{
                            fontSize: Typography.Fine,
                            color: '#94a3b8',
                          }}>
                            {new Date(bet.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{
                          paddingTop: '10px',
                          marginTop: '10px',
                          borderTop: '1px solid rgba(0,0,0,0.05)',
                        }}>
                          <button
                            onClick={() => handleCashOut(bet.id)}
                            disabled={cashingOutId === bet.id}
                            className="w-full py-2.5 flex items-center justify-center gap-2 transition-all duration-150"
                            style={{
                              background: cashingOutId === bet.id ? 'rgba(0,0,0,0.04)' : '#0f172a',
                              color: cashingOutId === bet.id ? '#94a3b8' : '#ffffff',
                              fontSize: Typography.Caption,
                              fontWeight: Typography.WeightBold,
                              borderRadius: '8px',
                              cursor: cashingOutId === bet.id ? 'not-allowed' : 'pointer',
                              opacity: cashingOutId && cashingOutId !== bet.id ? 0.5 : 1,
                            }}
                          >
                            {cashingOutId === bet.id ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Cashing Out...
                              </>
                            ) : (
                              <>
                                <Banknote size={14} />
                                Cash Out ~${(currentVal * 0.99).toFixed(2)}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {settledBets.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 mb-3" style={{
                  fontSize: Typography.BodyMedium,
                  fontWeight: Typography.WeightBold,
                  color: '#0f172a',
                }}>
                  <Trophy size={16} style={{ color: '#64748b' }} />
                  Settled ({settledBets.length})
                </h3>
                <div className="space-y-3">
                  {settledBets.map((bet) => {
                    const netPnl = bet.payout - bet.amount;
                    return (
                      <div
                        key={bet.id}
                        className="p-4"
                        style={{
                          background: '#ffffff',
                          border: `1px solid ${bet.settlement_status === 'won' ? 'rgba(16,185,129,0.2)' : bet.settlement_status === 'cashed_out' ? 'rgba(59,130,246,0.2)' : 'rgba(239,68,68,0.12)'}`,
                          borderRadius: '12px',
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {bet.settlement_status === 'won' ? (
                                <CheckCircle2 size={14} style={{ color: '#10b981' }} />
                              ) : bet.settlement_status === 'cashed_out' ? (
                                <Banknote size={14} style={{ color: '#3b82f6' }} />
                              ) : (
                                <XCircle size={14} style={{ color: '#ef4444' }} />
                              )}
                              <span style={{
                                fontSize: Typography.Caption,
                                fontWeight: Typography.WeightBold,
                                color: bet.settlement_status === 'won' ? '#059669' : bet.settlement_status === 'cashed_out' ? '#3b82f6' : '#dc2626',
                              }}>
                                {bet.settlement_status === 'won' ? 'Won' : bet.settlement_status === 'cashed_out' ? 'Cashed Out' : 'Lost'}
                              </span>
                            </div>
                            <p style={{
                              fontSize: Typography.BodySmall,
                              color: '#475569',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {bet.event_question || 'Event'}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5" style={{
                                background: bet.chosen_outcome === 'yes'
                                  ? 'rgba(16, 185, 129, 0.1)'
                                  : 'rgba(239, 68, 68, 0.08)',
                                color: bet.chosen_outcome === 'yes' ? '#059669' : '#dc2626',
                                fontSize: Typography.Fine,
                                fontWeight: Typography.WeightBold,
                                borderRadius: '4px',
                              }}>
                                {bet.chosen_outcome?.toUpperCase()}
                              </span>
                              <span className="font-mono" style={{
                                fontSize: Typography.Fine,
                                color: '#94a3b8',
                              }}>
                                {bet.settled_at ? new Date(bet.settled_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono" style={{
                              fontSize: Typography.BodyMedium,
                              fontWeight: Typography.WeightBold,
                              color: netPnl >= 0 ? '#059669' : '#dc2626',
                            }}>
                              {netPnl >= 0 ? '+' : ''}{netPnl.toFixed(2)}
                            </div>
                            <div className="font-mono" style={{
                              fontSize: Typography.Fine,
                              color: '#94a3b8',
                            }}>
                              Bet: ${bet.amount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {myBets.length === 0 && (
              <div className="text-center py-16">
                <TrendingUp
                  size={40}
                  style={{ color: '#cbd5e1', margin: '0 auto 1rem' }}
                />
                <h3
                  className="mb-2"
                  style={{
                    fontSize: Typography.HeadlineSmall,
                    fontWeight: Typography.WeightBold,
                    color: '#475569',
                  }}
                >
                  No bets yet
                </h3>
                <p style={{ fontSize: Typography.BodySmall, color: '#94a3b8', marginBottom: '16px' }}>
                  Place your first bet on a prediction market
                </p>
                <button
                  onClick={() => setActiveTab('markets')}
                  className="px-5 py-2.5"
                  style={{
                    background: '#d91222',
                    color: '#ffffff',
                    fontSize: Typography.Caption,
                    fontWeight: Typography.WeightBold,
                    borderRadius: '8px',
                  }}
                >
                  Browse Markets
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PolymarketEventBetting;
